import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { styles } from './VehicleStart.styles';
import Typography from 'src/components/Text/Text';
import InputField from 'src/components/input/input';
import Divider from 'src/components/Divider/Divider';
import Button from 'src/components/Button/Button';
import CameraCard from 'src/components/Cards/CameraCard';
import ImagePreview from 'src/components/ImagePreview/ImagePreview';
import Toast from 'src/components/Toast/Toast';
import TourWrapper from 'src/components/TourWrapper/TourWrapper';
import { useTourAutoStart } from 'src/features/tour/hooks/useTourAutoStart';
import { getVehicleStartTour } from 'src/features/tour/tourSteps';

import { useLinkTo, useRoute } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentVehicle } from 'src/features/vehicles/slice/vehicleSlice';
import {
  useLazyDecodeVinQuery,
  useUploadVinImageMutation,
} from 'src/features/vehicles/api/vehicleApi';
import { toggleToastState } from 'src/helpers/toastHelper';

import { RootState } from 'src/store/store';
import { URLS } from 'src/constants/urls';
import { setActiveTab } from 'src/slices/UiSlice';
import { TABS_INDEX } from 'src/constants/tabs';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents';
import { isVinDecoderResponseValid } from 'src/features/vehicles/helpers/vehicleHelpers'; // Import the helper function

const VehicleStart: React.FC = () => {
  const [vin, setVin] = useState('');
  const [previousVin, setPreviousVin] = useState(''); // Track previous VIN before edit
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [triggerDecodeVin] = useLazyDecodeVinQuery();
  const [uploadVinImage] = useUploadVinImageMutation();
  const [isVinDataExtracted, setIsVinDataExtracted] = useState(false);
  const [vinExists, setVinExists] = useState(false);
  const [vinExtractErrorToast, setVinExtractedErrorToast] = useState(false);

  const linkTo = useLinkTo();
  const dispatch = useDispatch();
  const route = useRoute();

  const { id } = route.params as { id?: number };

  const currentVehicle = useSelector((state: RootState) => state.vehicles.currentVehicle);
  const vehicles = useSelector((state: RootState) => state.vehicles.vehicles);
  const accountType = useSelector((state: RootState) => state.UI.accountType);

  useEffect(() => {
    if (currentVehicle) {
      setVin(currentVehicle.vin || '');
    }
  }, [currentVehicle]);

  useEffect(() => {
    // Track the event for when the user lands on the add vehicle page
    trackGTMEvent(
      'vehicle-start-screen-landed',
      {
        userEvent: 'user lands on vehicle start screen',
      },
      accountType,
    );
  }, []);

  // Auto-start tour on first visit
  useTourAutoStart({
    tourKey: 'vehicleStart',
    driverSteps: getVehicleStartTour(),
    enabled: vin !== undefined,
  });

  const handleSaveVehicle = async () => {
    const doesVinExist = vehicles.some((vehicle) => vehicle.vin === vin);
    setVinExists(doesVinExist);
    if (doesVinExist) {
      const timeout = setTimeout(() => {
        setVinExists(false);
      }, 3000);
      return () => clearTimeout(timeout); // Cleanup timeout
    }
    try {
      const data = await triggerDecodeVin(vin).unwrap();
      dispatch(
        setCurrentVehicle({
          ...currentVehicle,
          vin,
          make: data?.make,
          model: data?.model,
          year: data?.year,
          classCode: data?.classCode,
          type: data?.type === 'trailer' ? 'trailer' : 'truck',
        }),
      );

      if (isVinDecoderResponseValid(data)) {
        // **Track VIN Decoded Event**
        trackGTMEvent(
          'vin-decoded',
          {
            userEvent: 'VIN Decoded Successfully',
          },
          accountType,
        );
      } else {
        throw new Error('Invalid VIN Decoder Response');
      }

      linkTo(URLS.VEHICLE.ADD);
    } catch {
      dispatch(setCurrentVehicle({ ...currentVehicle, vin }));

      // **Track VIN Decode Error Event**
      trackGTMEvent(
        'vin-decode-error',
        {
          userEvent: 'VIN Decoding Failed',
        },
        accountType,
      );

      if (isVinDataExtracted) {
        linkTo(URLS.VEHICLE.ADD);
      }
      setIsVinDataExtracted(true);
    }
  };

  const handleInputChange = (text: string) => {
    // Check for invalid characters in VIN
    if (/[^A-Za-z0-9]/.test(text)) {
      return;
    }
    setVin(text); // Update VIN state with cleaned value
  };

  const handleVinBlur = () => {
    if (vin !== previousVin) {
      trackGTMEvent(
        'vin-input-field-edited',
        {
          userEvent: 'User edited the VIN input field',
          screenName: 'Vehicle Start Screen',
          previousVin: previousVin || 'Empty',
          newVin: vin,
          editType: isVinDataExtracted ? 'Edited Extracted VIN' : 'Manual Entry',
        },
        accountType,
      );

      setIsVinDataExtracted(false); // Reset extraction flag
      setPreviousVin(vin); // Update previous VIN after tracking
    }
  };

  const handleVinImageUpload = async () => {
    if (!selectedImage) {
      return;
    }

    const formData = new FormData();
    const imageUri = selectedImage;

    // Create a Blob or file from the selected image (ensure the correct type)
    const imageFile = await fetch(imageUri);
    const blob = await imageFile.blob();
    formData.append('image', blob, 'vehicle-image.jpg'); // Add the image to the FormData object

    try {
      // gtm event for user add iamge and vin extractor api is called
      trackGTMEvent(
        'vin-extractor-image-uploaded',
        {
          userEvent: 'User uploaded an image of VIN, triggering VIN Extractor API',
        },
        accountType,
      );

      const data = await uploadVinImage(formData).unwrap();
      if (data.vin && data.vin.length > 0) {
        setVin(data.vin || '');

        // Track the successful VIN extraction event**
        trackGTMEvent(
          'vin-extracted',
          {
            userEvent: 'VIN extractor ran successfully and extracted VIN',
          },
          accountType,
        );
        return;
      }
      setVinExtractedErrorToast(true);
      toggleToastState(setVinExtractedErrorToast);
    } catch (error) {
      // Track the error event for VIN extraction
      trackGTMEvent(
        'vin-extraction-error',
        {
          userEvent: 'VIN extractor failed to extract VIN from image',
        },
        accountType,
      );

      setVinExtractedErrorToast(true);
      setVin('');
      toggleToastState(setVinExtractedErrorToast);

      console.error('Error uploading image:', error);
    }
  };
  useEffect(() => {
    if (selectedImage) {
      handleVinImageUpload(); // Upload image when selectedImage is set
    }
  }, [selectedImage]);

  const handleBack = () => {
    if (vehicles.length > 0) {
      // When vehicles exist, navigate to the vehicle bypass screen
      linkTo(URLS.VEHICLE.BYPASS);
      trackGTMEvent(
        'vehicle-start-screen-left-to-bypass-screen',
        {
          buttonTitle: 'Back',
          userEvent: 'User returned to summary screen from Vehicle Start Screen',
        },
        accountType,
      );
    } else {
      // When no vehicles exist, navigate to the company landing screen
      linkTo(URLS.COMPANY.LANDING);
      trackGTMEvent(
        'vehicle-start-screen-left-to-company-landing-screen',
        {
          buttonTitle: 'Back',
          userEvent: 'User returned to Company Landing Screen from Vehicle Start Screen',
        },
        accountType,
      );
      dispatch(setActiveTab({ activeTab: TABS_INDEX.COMPANY }));
    }
  };

  const handleImageSelected = (image: string) => {
    setSelectedImage(image); // Assign the selected image here
    trackGTMEvent(
      'vin-extractor-clicked',
      {
        buttonTitle: 'Upload VIN Image',
        userEvent: 'User clicked to upload VIN image for extraction',
      },
      accountType,
    );
  };

  const handleUpdateVehicle = async () => {
    const doesVinExist = vehicles.some(
      (vehicle) => vehicle.vin === vin && vehicle.vin !== currentVehicle?.vin,
    );
    setVinExists(doesVinExist);
    if (doesVinExist) {
      const timeout = setTimeout(() => {
        setVinExists(false);
      }, 3000);
      return () => clearTimeout(timeout); // Cleanup timeout
    }

    if (currentVehicle?.vin === vin) {
      dispatch(setCurrentVehicle({ ...currentVehicle, vin }));
      linkTo(`${URLS.VEHICLE.EDIT}/${id}`);
    } else {
      try {
        const data = await triggerDecodeVin(vin).unwrap();
        dispatch(
          setCurrentVehicle({
            ...currentVehicle,
            vin,
            make: data?.make,
            model: data?.model,
            year: data?.year,
            classCode: data?.classCode,
            type: data?.type === 'trailer' ? 'trailer' : 'truck',
            radiusTravelled: '',
            value: {
              amount: '',
              currency: 'USD',
            },
            equipmentValue: {
              amount: '',
              currency: 'USD',
            },
            bodyType: '',
          }),
        );

        if (isVinDecoderResponseValid(data)) {
          // **Track VIN Decoded Event**
          trackGTMEvent(
            'vin-decoded',
            {
              userEvent: 'VIN Decoded Successfully',
            },
            accountType,
          );
        }

        linkTo(`${URLS.VEHICLE.EDIT}/${id}`);
      } catch {
        dispatch(setCurrentVehicle({ ...currentVehicle, vin }));

        // **Track VIN Decode Error Event**
        trackGTMEvent(
          'vin-decode-error',
          {
            userEvent: 'VIN Decoding Failed',
          },
          accountType,
        );

        if (isVinDataExtracted) {
          linkTo(`${URLS.VEHICLE.EDIT}/${id}`);
        }
        setIsVinDataExtracted(true);
      }
    }
  };
  const handleNext = () => {
    if (id) {
      handleUpdateVehicle();
    } else {
      handleSaveVehicle();
    }

    // gtm event when next is pressed
    trackGTMEvent(
      'vehicle-start-screen-vin-confirmed',
      {
        buttonTitle: 'Next',
        userEvent: 'user pressed next button after filling the vin field',
      },
      accountType,
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.topContainer}>
        <Typography variant="h2">Your VIN</Typography>
        <TourWrapper tourId="vin-input">
          <InputField
            testID="vehicle-vin-input"
            variant="caption"
            label={'VIN'}
            value={vin}
            onBlur={handleVinBlur}
            onChange={(text: string) => handleInputChange(text)}
            maxLength={17}
          />
        </TourWrapper>
        <Divider text=" Or " />
        {!selectedImage && (
          <TourWrapper tourId="upload-vin-card">
            <CameraCard
              testID="upload-vin-image-button"
              text="Upload a photo of your VIN"
              fileType="image"
              onImageSelected={(imageUri) => handleImageSelected(imageUri as string)}
            />
          </TourWrapper>
        )}
        {selectedImage && (
          <ImagePreview
            source={selectedImage}
            onIconPress={() => setSelectedImage(null)}
            testID="vin-image-preview"
          />
        )}
      </View>
      <View>
        {(isVinDataExtracted || vinExists) && (
          <Toast
            testID={vinExists ? 'toast-vin-exists' : 'toast-vin-fetch-failed'}
            text={
              vinExists
                ? 'A vehicle with this VIN has already been added to the list.'
                : 'We were unable to fetch vehicle details.'
            }
            type={vinExists ? 'error' : 'warning'}
            animate={true}
          />
        )}

        {vinExtractErrorToast && (
          <Toast
            type="error"
            text="An error occurred while extracting VIN from given image. Please upload a valid image or input VIN manually."
            animate={true}
          />
        )}
        <View style={styles.buttonContainer}>
          <View style={styles.buttonWrapper1}>
            <Button
              testID="vehicle-vin-upload-back"
              type="tertiary"
              title="Back"
              onPress={handleBack}
            />
          </View>
          <View style={styles.buttonWrapper2}>
            <TourWrapper tourId="vehicle-start-next-btn">
              <Button
                testID="vehicle-vin-upload-next"
                type="primary"
                colorVariant={vin.length >= 10 ? 'default' : 'disabled'}
                title="Next"
                onPress={handleNext}
              />
            </TourWrapper>
          </View>
        </View>
      </View>
    </View>
  );
};

export default VehicleStart;
